<template>
  <div class="vehicle-map-page">
    <Header :title="title" :showBack="true"></Header>
    <div class="vehicle-map-body">
      <div id="vehicle-map-container" class="vehicle-map-canvas"></div>

    <div class="vehicle-map-top">
      <div class="vehicle-map-top__row">
        <div class="vehicle-map-card">
          <div class="vehicle-map-card__title">
            {{ (lineClassDay && lineClassDay.LineName) || "车辆位置" }}
          </div>
          <div class="vehicle-map-card__sub">{{ lineSubTitle }}</div>
        </div>
        <div class="vehicle-map-refresh" @click="refreshSignalR">
          <van-icon name="replay" size="22" />
        </div>
      </div>

      <div v-if="showSignalRBanner" class="vehicle-map-banner">
        <van-icon name="warning-o" size="18" color="#fa8c16" />
        <span>{{ signalRBannerText }}</span>
      </div>

      <div
        v-if="vehicleGIDList && !isClassTaskFinished"
        class="vehicle-map-gps"
        :class="{ 'is-pulse': gpsUpdatePulse }"
        @click="refreshLastGpsUpdateText"
      >
        {{
          vehiclePositions.length
            ? "实时定位 " + (lastGpsUpdateText || "刚刚更新")
            : "正在持续查询车辆位置..."
        }}
      </div>
    </div>

    <div class="vehicle-map-controls">
      <div class="vehicle-map-ctrl" @click="zoomIn">+</div>
      <div class="vehicle-map-ctrl" @click="zoomOut">−</div>
      <div class="vehicle-map-ctrl vehicle-map-ctrl--bus" @click="recenterToVehicle">
        <span class="iconfont iconbus--fill" style="color:#fff"></span>
      </div>
    </div>

    <div v-if="selectedStation" class="vehicle-map-bottom">
      <div class="vehicle-map-station" @click="openStationPicker">
        <van-icon name="location-o" size="18" color="#1677ff" />
        <span class="vehicle-map-station__name">{{ selectedStation.name }}</span>
        <van-icon name="arrow" size="16" color="#c8c8c8" />
      </div>
      <div class="vehicle-map-divider"></div>

      <div v-if="!stationVehicleRows.length" class="vehicle-map-empty">
        暂无到站车辆
      </div>

      <div
        v-for="(row, index) in stationVehicleRows"
        :key="row.vehicleGid || row.vehicleNo || index"
        :class="['vehicle-map-row', index > 0 ? 'is-split' : '']"
      >
        <div class="vehicle-map-row__head">
          <span class="iconfont iconbus--fill vehicle-map-row__bus"></span>
          <span class="vehicle-map-row__plate">
            {{ row.vehicleNo || "—" }}
            <span v-if="row.seatCount != null" class="vehicle-map-row__seat">
              ({{ row.seatCount }})
            </span>
          </span>
          <span
            v-if="row.directionText || row.speedText"
            class="vehicle-map-row__motion"
          >
            <span v-if="row.directionText">向{{ row.directionText }}</span>
            <span v-if="row.speedText">{{ row.speedText }}</span>
          </span>
        </div>

        <div
          v-if="row.driverName || row.driverTel"
          class="vehicle-map-row__driver"
        >
          <van-icon name="manager" size="16" color="#b8b8b8" />
          <span class="vehicle-map-row__driver-name">{{
            row.driverName || "司机"
          }}</span>
          <a
            v-if="row.driverTel"
            class="vehicle-map-row__tel"
            :href="'tel:' + row.driverTel"
            @click.stop
          >
            <van-icon name="phone" size="16" color="#3c9cff" />
            <span>{{ row.driverTel }}</span>
          </a>
        </div>

        <div class="vehicle-map-row__status">
          <van-tag
            plain
            :type="
              row.status === 'arrived'
                ? 'success'
                : row.status === 'incoming'
                ? 'primary'
                : row.status === 'enroute'
                ? 'default'
                : 'warning'
            "
          >
            {{ row.statusLabel }}
          </van-tag>
          <span
            v-if="
              (row.status === 'incoming' || row.status === 'enroute') &&
                row.etaText
            "
            class="vehicle-map-row__eta"
          >
            {{ row.etaText }}
          </span>
        </div>
      </div>
    </div>
    </div>

    <van-popup
      v-model="showStationPicker"
      position="bottom"
      round
      :get-container="getStationPickerContainer"
      :style="{ height: '72%' }"
    >
      <div class="station-picker">
        <div class="station-picker__header">
          <div class="station-picker__title">选择站点</div>
          <div
            class="station-picker__close"
            @click="showStationPicker = false"
          >
            <van-icon name="cross" size="20" color="#969799" />
          </div>
        </div>
        <div class="station-picker__list">
          <div
            v-for="station in stations"
            :key="station.id"
            class="station-picker__item"
            :class="{ 'is-selected': station.id === selectedStationId }"
            @click="onPickStation(station)"
          >
            <div class="station-picker__main">
              <div class="station-picker__name">{{ station.name }}</div>
              <van-tag plain :type="station.role === 'arrival' ? 'danger' : 'success'" size="medium">
                {{ station.role === "arrival" ? "下车点" : "上车点" }}
              </van-tag>
            </div>
            <van-icon
              :name="station.id === selectedStationId ? 'success' : 'arrow'"
              :color="station.id === selectedStationId ? '#1677ff' : '#c8c8c8'"
              size="18"
            />
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Header from "@/components/Header/Header.vue";
import { VehicleWebSocket } from "@/js/websocket";
import startStationIcon from "@/img/start.png";
import endStationIcon from "@/img/end.png";
import {
  AMAP_ETA_ROUTE_STRATEGY,
  estimateDurationByDistanceM,
  fetchAmapDrivingRouteChain,
  formatDistanceKm,
  formatDurationText,
  haversineDistanceM,
} from "./amap-driving";
import {
  VEHICLE_MAP_PREVIEW_STORAGE_KEY,
  bearingDegrees,
  buildEtaRoutePointsToStation,
  buildOrderedMapStations,
  estimateRouteChainDistanceM,
  findMapStationByNodeGid,
  findNearestMapStation,
  findVehicleStationDepartRecord,
  geoBearingToVehicleAngle,
  hasReachedOrPassedStationOnSegment,
  hasVehiclePassedStationByRoute,
  headingAngleDiff,
  isStationSchedulePassed,
  isVehicleApproachingOnStationSegment,
  isVehicleArrivedAtStationByDepartRecord,
  isVehiclePassedStationByLaterDepartRecords,
  mergeMapArrivalWithQueryList,
  normalizeGpsPosition,
  parseMapNodeList,
} from "./vehicle-map.utils";

var DEFAULT_LAT = 23.129155;
var DEFAULT_LNG = 113.264434;
var STATION_PASSED_M = 80;
var STATION_INCOMING_M = 1000;
var STATION_DISPLAY_ARRIVED_M = 100;
var STATION_DISPLAY_ARRIVED_MAX_SPEED_KMH = 10;
var STATUS_LABEL_ARRIVED = "车辆已到站";
var STATUS_LABEL_ARRIVED_DEPARTED = "车辆已到站并发车";
var CLASS_TASK_END_BUFFER_HOURS = 2;
// 与旧版 VehicleMap 相同：只加载 JSAPI 1.4.12，不使用小程序项目的 2.0。
var AMAP_KEY = "24f21e97ad61f5d65b50a38c175b3fe4";
var AMAP_SECURITY_CODE = "c6bcd7dc52ae28e9567c690ba7939b72";
var AMAP_SCRIPT_ID = "amap-jsapi-1.4";
var AMAP_BUS_ICON = "https://webapi.amap.com/images/car.png";
var STATION_ICON_SIZE = 32;

function readJson(key, fallback) {
  try {
    var raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

function pad2(n) {
  return n < 10 ? "0" + n : String(n);
}

function formatHms(date) {
  return pad2(date.getHours()) + ":" + pad2(date.getMinutes()) + ":" + pad2(date.getSeconds());
}

function formatYmd(date) {
  return date.getFullYear() + "-" + pad2(date.getMonth() + 1) + "-" + pad2(date.getDate());
}

function parseDateTime(str) {
  if (str == null || str === "") return null;
  if (str instanceof Date) return isNaN(str.getTime()) ? null : str;
  if (typeof str === "number" && isFinite(str)) {
    var ms = str < 1e12 ? str * 1000 : str;
    var fromNum = new Date(ms);
    return isNaN(fromNum.getTime()) ? null : fromNum;
  }
  var s = String(str).trim();
  if (!s) return null;
  var msJson = /\/Date\((-?\d+)(?:[+-]\d+)?\)\//.exec(s);
  if (msJson) {
    var fromJson = new Date(Number(msJson[1]));
    return isNaN(fromJson.getTime()) ? null : fromJson;
  }
  var normalized = s.replace(/-/g, "/").replace("T", " ").replace(/\.\d+Z?$/, "");
  var d = new Date(normalized);
  if (!isNaN(d.getTime())) return d;
  var fallback = new Date(s);
  return isNaN(fallback.getTime()) ? null : fallback;
}

function pickGpsTime(item) {
  if (!item) return "";
  var nested = item.Data || item.data || {};
  var raw =
    item.Time ||
    item.time ||
    item.GpsTime ||
    item.gpsTime ||
    nested.Time ||
    nested.time ||
    nested.GpsTime;
  return raw == null || raw === "" ? "" : raw;
}

export default {
  name: "VehicleMap",
  components: {
    Header,
  },
  data: function () {
    return {
      title: "车辆位置",
      lineClassDay: readJson("lineClassDay", null),
      vehicleGIDList: "",
      mapEntry: "",
      focusNodeGID: "",
      wsTitle: "正在连接车辆定位服务...",
      signalRStatus: "connecting",
      classMapData: null,
      stations: [],
      classOperatingEndAt: null,
      arrivalNodeQueryList: [],
      selectedStationId: "",
      departRecords: [],
      departRecordsReady: false,
      vehiclePreviewList: [],
      vehiclePositions: [],
      vehicleApproachCache: {},
      approachRefreshTokens: {},
      approachTimeTick: 0,
      passedStickyMap: {},
      stationMinDistMap: {},
      lastGpsUpdateAt: "",
      lastGpsReceivedAt: 0,
      lastGpsUpdateText: "",
      gpsUpdatePulse: false,
      isManualCenter: false,
      hasInitialStationFocus: false,
      mapZoom: 14,
      pageDestroyed: false,
      signalRCloseSilentCount: 0,
      signalRDisconnectModalShown: false,
      nearestStationRetry: false,
      showStationPicker: false,
    };
  },
  computed: {
    mapStartNodes: function () {
      return parseMapNodeList(this.classMapData && this.classMapData.StartNodeList);
    },
    mapArrivalNodes: function () {
      return parseMapNodeList(this.classMapData && this.classMapData.ArrivalNodeList);
    },
    lineSubTitle: function () {
      var ordered = this.stations;
      if (!ordered.length) return "";
      return ordered[0].name + " → " + ordered[ordered.length - 1].name;
    },
    isClassTaskFinished: function () {
      if (!this.classOperatingEndAt) return false;
      return Date.now() > this.classOperatingEndAt + CLASS_TASK_END_BUFFER_HOURS * 3600 * 1000;
    },
    showSignalRBanner: function () {
      if (this.isClassTaskFinished) return false;
      return this.signalRStatus === "disconnected" || this.signalRStatus === "error";
    },
    signalRBannerText: function () {
      if (this.signalRStatus === "error") {
        return "定位服务连接异常，车辆位置可能无法更新，请点击刷新重新获取";
      }
      return "实时定位已断开，车辆位置可能无法更新，请点击刷新重新获取";
    },
    selectedStation: function () {
      var id = this.selectedStationId;
      var list = this.stations;
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) return list[i];
      }
      return null;
    },
    isViewportLocked: function () {
      return this.hasInitialStationFocus || this.isManualCenter;
    },
    stationVehicleRows: function () {
      this.approachTimeTick;
      var selected = this.selectedStation;
      if (!selected) return [];
      var gpsList = this.vehiclePositions || [];
      var previews = this.vehiclePreviewList.length
        ? this.vehiclePreviewList
        : gpsList.map(function (gps) {
            return { VehicleGID: gps.VehicleGID, VehicleNo: gps.VehicleNo };
          });
      var self = this;
      var taskFinished = this.isClassTaskFinished;
      return previews.map(function (preview) {
        return self.buildStationVehicleRow(preview, gpsList, selected, taskFinished);
      });
    },
  },
  watch: {
    selectedStationId: function () {
      if (this.isClassTaskFinished) return;
      this.scheduleVehicleApproachRefresh(this.vehiclePositions);
      this.renderMarkers();
    },
    vehiclePositions: function () {
      this.renderMarkers();
    },
    stations: function () {
      this.renderMarkers();
    },
  },
  created: function () {
    this.vehicleGIDList = (this.$route.query.VehicleGIDList || "").trim();
    this.mapEntry = (this.$route.query.entry || "").trim();
    this.focusNodeGID = (this.$route.query.focusNodeGID || "").trim();
    this.vehiclePreviewList = this.buildVehiclePreviewList(this.vehicleGIDList);
  },
  mounted: function () {
    this.initPage();
  },
  beforeDestroy: function () {
    this.pageDestroyed = true;
    this.closeSignalR(true);
    if (this.gpsUpdateTimer) clearInterval(this.gpsUpdateTimer);
    if (this.approachTimeTimer) clearInterval(this.approachTimeTimer);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.map) {
      if (this.map.destroy) this.map.destroy();
      this.map = null;
    }
  },
  methods: {
    postApi: function (url, params) {
      return this.$http.post(url, this.$qs.stringify(params || {}));
    },
    normalizeGid: function (gid) {
      return String(gid || "").trim().toUpperCase();
    },
    buildVehiclePreviewList: function (gidList) {
      var cached = readJson(VEHICLE_MAP_PREVIEW_STORAGE_KEY, null);
      var cachedList = Array.isArray(cached) ? cached : cached ? [cached] : [];
      var gids = String(gidList || "")
        .split(",")
        .map(function (gid) {
          return gid.trim();
        })
        .filter(Boolean);
      if (!gids.length) return cachedList;
      return gids.map(function (gid) {
        var key = gid.toUpperCase();
        var hit = null;
        for (var i = 0; i < cachedList.length; i++) {
          if (String(cachedList[i].VehicleGID || "").trim().toUpperCase() === key) {
            hit = cachedList[i];
            break;
          }
        }
        return {
          VehicleGID: (hit && hit.VehicleGID) || gid,
          VehicleNo: hit && hit.VehicleNo,
          PassengerSeatCount: hit && hit.PassengerSeatCount,
          Driver1Name: hit && hit.Driver1Name,
          Driver1Tel: hit && hit.Driver1Tel,
          Driver2Name: hit && hit.Driver2Name,
          Driver2Tel: hit && hit.Driver2Tel,
        };
      });
    },
    mergePreviewWithDrivers: function (previews, drivers) {
      var map = {};
      var self = this;
      (drivers || []).forEach(function (item) {
        var key = self.normalizeGid(item.VehicleGID);
        if (key) map[key] = item;
      });
      if (previews.length) {
        return previews.map(function (preview) {
          var driver = map[self.normalizeGid(preview.VehicleGID)];
          if (!driver) return preview;
          return {
            VehicleGID: preview.VehicleGID,
            VehicleNo: preview.VehicleNo || driver.VehicleNo,
            PassengerSeatCount:
              preview.PassengerSeatCount != null
                ? preview.PassengerSeatCount
                : driver.PassengerSeatCount,
            Driver1Name: driver.Driver1Name || preview.Driver1Name,
            Driver1Tel: driver.Driver1Tel || preview.Driver1Tel,
            Driver2Name: driver.Driver2Name || preview.Driver2Name,
            Driver2Tel: driver.Driver2Tel || preview.Driver2Tel,
          };
        });
      }
      return (drivers || []).map(function (driver) {
        return {
          VehicleGID: driver.VehicleGID,
          VehicleNo: driver.VehicleNo,
          PassengerSeatCount: driver.PassengerSeatCount,
          Driver1Name: driver.Driver1Name,
          Driver1Tel: driver.Driver1Tel,
          Driver2Name: driver.Driver2Name,
          Driver2Tel: driver.Driver2Tel,
        };
      });
    },
    initPage: function () {
      var self = this;
      var driverTask = this.loadVehicleDriverPreview();
      this.loadAMap()
        .then(function () {
          return self.loadClassMapData();
        })
        .then(function () {
          return self.loadStations();
        })
        .then(function () {
          return self.resolveDefaultSelectedStation(self.focusNodeGID);
        })
        .then(function () {
          return driverTask;
        })
        .then(function () {
          self.renderMarkers();
          if (self.isClassTaskFinished) {
            self.signalRStatus = "ended";
            self.wsTitle = "本班次任务已结束，不再拉取实时定位";
            return;
          }
          self.initSignalR();
          self.approachTimeTimer = setInterval(function () {
            self.approachTimeTick += 1;
            if (self.isClassTaskFinished) {
              self.closeSignalR(true);
              self.signalRStatus = "ended";
              self.wsTitle = "本班次任务已结束，不再拉取实时定位";
              if (self.approachTimeTimer) {
                clearInterval(self.approachTimeTimer);
                self.approachTimeTimer = null;
              }
            }
          }, 60000);
        })
        .catch(function (err) {
          console.error("车辆地图初始化失败", err);
          self.$toast("地图初始化失败");
        });
    },
    isAMap2: function (AMap) {
      if (!AMap) return false;
      var v = String(AMap.v || AMap.version || "");
      return v.indexOf("2.") === 0;
    },
    refreshMapSize: function (map) {
      if (!map) return;
      try {
        if (window.AMap && window.AMap.event && window.AMap.event.trigger) {
          window.AMap.event.trigger(map, "resize");
        }
      } catch (e) {}
    },
    escapeHtml: function (str) {
      return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    buildMapLabelHtml: function (text) {
      return '<div class="vm-map-label">' + this.escapeHtml(text) + "</div>";
    },
    buildVehicleMarkerHtml: function (item, angle) {
      var plate = item && item.VehicleNo
        ? '<div class="vm-vehicle-plate">' + this.escapeHtml(item.VehicleNo) + "</div>"
        : "";
      var deg = isFinite(angle) ? angle : 0;
      return (
        '<div class="vm-vehicle-marker">' +
        plate +
        '<div class="vm-vehicle-icon-box">' +
        '<img src="' +
        AMAP_BUS_ICON +
        '" alt="" style="transform:rotate(' +
        deg +
        'deg)" />' +
        "</div></div>"
      );
    },
    buildStationMarkerHtml: function (st) {
      var src = st.role === "arrival" ? endStationIcon : startStationIcon;
      var name = st.name
        ? '<div class="vm-station-name">' + this.escapeHtml(st.name) + "</div>"
        : "";
      return (
        '<div class="vm-station-marker">' +
        name +
        '<img class="vm-station-marker__icon" src="' +
        src +
        '" alt="" />' +
        "</div>"
      );
    },
    loadAMap: function () {
      var self = this;
      if (this._amapLoadPromise) return this._amapLoadPromise;
      // 完全沿用旧版 VehicleMap：JSAPI 1.4.12 + 原 key。不要走小程序项目的 2.0。
      window._AMapSecurityConfig = {
        securityJsCode: AMAP_SECURITY_CODE,
      };
      this._amapLoadPromise = new Promise(function (resolve, reject) {
        function createMap() {
          var AMap = window.AMap;
          if (!AMap || !AMap.Map) {
            reject(new Error("高德地图未加载"));
            return;
          }
          if (self.isAMap2(AMap)) {
            reject(new Error("当前为高德 2.0，已禁用"));
            return;
          }
          self.AMap = AMap;
          self.$nextTick(function () {
            if (self.pageDestroyed) return;
            if (!document.getElementById("vehicle-map-container")) {
              reject(new Error("地图容器不存在"));
              return;
            }
            if (self.map) {
              resolve(AMap);
              return;
            }
            self.map = new AMap.Map("vehicle-map-container", {
              resizeEnable: true,
              zoom: 13,
              center: [DEFAULT_LNG, DEFAULT_LAT],
            });
            self.stationMarkers = [];
            self.vehicleMarkers = [];
            if (AMap.plugin && !AMap.Driving) {
              AMap.plugin(["AMap.Driving"], function () {});
            }
            setTimeout(function () {
              self.refreshMapSize(self.map);
            }, 200);
            resolve(AMap);
          });
        }
        if (window.AMap && window.AMap.Map && !self.isAMap2(window.AMap)) {
          createMap();
          return;
        }
        try {
          window.AMap = undefined;
        } catch (e) {}
        window.onVehicleMapAMapLoaded = function () {
          createMap();
        };
        var existing = document.getElementById(AMAP_SCRIPT_ID);
        if (existing && existing.parentNode) {
          existing.parentNode.removeChild(existing);
        }
        var script = document.createElement("script");
        script.id = AMAP_SCRIPT_ID;
        script.charset = "utf-8";
        script.src =
          "https://webapi.amap.com/maps?v=1.4.12&key=" +
          AMAP_KEY +
          "&callback=onVehicleMapAMapLoaded";
        script.onerror = function () {
          self._amapLoadPromise = null;
          reject(new Error("高德地图脚本加载失败"));
        };
        document.head.appendChild(script);
      });
      return this._amapLoadPromise;
    },
    loadClassMapData: function () {
      var self = this;
      var gid = this.lineClassDay && this.lineClassDay.GID;
      if (!gid) {
        this.wsTitle = "获取班次地图数据失败";
        return Promise.resolve(false);
      }
      return this.postApi(this.$api.getMapData, { LineClassDayGID: gid })
        .then(function (res) {
          if (!res.success || !res.data || !res.data[0]) {
            self.wsTitle = "获取班次地图数据失败";
            return false;
          }
          self.classMapData = res.data[0];
          if (!self.vehicleGIDList && self.classMapData.VehicleGIDList) {
            self.vehicleGIDList = String(self.classMapData.VehicleGIDList);
            self.vehiclePreviewList = self.buildVehiclePreviewList(self.vehicleGIDList);
          }
          return true;
        })
        .catch(function (err) {
          console.error("获取班次地图数据失败", err);
          self.wsTitle = "获取班次地图数据失败";
          return false;
        });
    },
    loadStations: function () {
      var self = this;
      var gid = this.lineClassDay && this.lineClassDay.GID;
      if (!gid) {
        this.stations = [];
        this.departRecordsReady = true;
        return Promise.resolve();
      }
      this.departRecordsReady = false;
      var startNodes = this.mapStartNodes;
      var mapArrivals = this.mapArrivalNodes;
      var arrivalPromise = mapArrivals.length
        ? this.postApi(this.$api.getArrivalNode, { LineClassDayGID: gid })
        : Promise.resolve({ data: [] });
      return arrivalPromise
        .then(function (res) {
          var arrivalQueryData = (res && res.data) || [];
          var mergedArrivals = mapArrivals.length
            ? mergeMapArrivalWithQueryList(mapArrivals, arrivalQueryData)
            : mapArrivals;
          self.arrivalNodeQueryList = arrivalQueryData;
          self.syncClassOperatingWindow(arrivalQueryData);
          self.stations = buildOrderedMapStations(startNodes, mergedArrivals);
          self.vehicleApproachCache = {};
          self.passedStickyMap = {};
          self.stationMinDistMap = {};
          return self.loadDepartRecords();
        })
        .catch(function (err) {
          console.warn("加载班次上下车站点失败", err);
          self.stations = [];
          self.departRecords = [];
          self.departRecordsReady = true;
        });
    },
    loadDepartRecords: function () {
      var self = this;
      var gid = this.lineClassDay && this.lineClassDay.GID;
      if (!gid) {
        this.departRecords = [];
        this.departRecordsReady = true;
        return Promise.resolve();
      }
      return this.postApi(this.$api.getLineClassDayDepartureRecord, {
        LineClassDayGID: gid,
      })
        .then(function (res) {
          self.departRecords = (res && res.data) || [];
        })
        .catch(function (err) {
          console.warn("加载发车记录失败", err);
          self.departRecords = [];
        })
        .then(function () {
          self.departRecordsReady = true;
        });
    },
    loadVehicleDriverPreview: function () {
      var self = this;
      var gid = this.lineClassDay && this.lineClassDay.GID;
      if (!gid) return Promise.resolve();
      var targetGids = String(this.vehicleGIDList || "")
        .split(",")
        .map(function (item) {
          return self.normalizeGid(item);
        })
        .filter(Boolean);
      return this.postApi(this.$api.getVehicleDriver, { LineClassDayGID: gid })
        .then(function (res) {
          var list = (res && res.data) || [];
          if (!list.length) return;
          var matched = targetGids.length
            ? list.filter(function (item) {
                return targetGids.indexOf(self.normalizeGid(item.VehicleGID)) > -1;
              })
            : list;
          if (!matched.length) return;
          self.vehiclePreviewList = self.mergePreviewWithDrivers(
            self.vehiclePreviewList,
            matched
          );
        })
        .catch(function (err) {
          console.error("[车辆地图] 获取车辆司机信息失败", err);
        });
    },
    resolveClassOperatingStartAt: function () {
      if (!this.lineClassDay || !this.lineClassDay.ClassDate || !this.lineClassDay.ClassTime) {
        return null;
      }
      var datePart = String(this.lineClassDay.ClassDate).substring(0, 10);
      var timePart = String(this.lineClassDay.ClassTime).substring(0, 8);
      return parseDateTime(datePart + " " + timePart);
    },
    syncClassOperatingWindow: function (arrivalQueryData) {
      var start = this.resolveClassOperatingStartAt();
      if (!start) {
        this.classOperatingEndAt = null;
        return;
      }
      var maxPredict = 0;
      (arrivalQueryData || []).forEach(function (item) {
        var d = parseDateTime(item.PredictOutTime);
        if (d && d.getTime() > maxPredict) maxPredict = d.getTime();
      });
      if (maxPredict) {
        this.classOperatingEndAt = maxPredict;
        return;
      }
      var maxRunTime = 0;
      (arrivalQueryData || []).forEach(function (item) {
        var n = Number(item.RunTime || 0);
        if (n > maxRunTime) maxRunTime = n;
      });
      this.classOperatingEndAt = maxRunTime > 0 ? start.getTime() + maxRunTime * 60 * 1000 : null;
    },
    selectStationOnMap: function (station) {
      if (!station || !this.map) return;
      this.selectedStationId = station.id;
      this.isManualCenter = true;
      this.hasInitialStationFocus = true;
      this.mapZoom = 15;
      this.map.setZoomAndCenter(15, [station.lng, station.lat]);
    },
    openStationPicker: function () {
      if (!this.stations || !this.stations.length) {
        this.$toast("暂无站点");
        return;
      }
      this.showStationPicker = true;
    },
    getStationPickerContainer: function () {
      return document.body;
    },
    onPickStation: function (station) {
      this.showStationPicker = false;
      this.selectStationOnMap(station);
    },
    resolveDefaultSelectedStation: function (focusNodeGid) {
      var self = this;
      if (focusNodeGid) {
        var focused = findMapStationByNodeGid(this.stations, focusNodeGid);
        if (focused) {
          this.selectStationOnMap(focused);
          return Promise.resolve(true);
        }
      }
      if (this.mapEntry === "vehicleTab") {
        return this.applyNearestStationByUserLocation();
      }
      if (this.stations.length) {
        this.selectStationOnMap(this.stations[0]);
        return Promise.resolve(true);
      }
      return Promise.resolve(false);
    },
    getUserLocation: function () {
      var self = this;
      return new Promise(function (resolve) {
        if (self.AMap && self.AMap.Geolocation) {
          var geo = new self.AMap.Geolocation({
            enableHighAccuracy: true,
            timeout: 5000,
          });
          geo.getCurrentPosition(function (status, result) {
            if (status === "complete" && result && result.position) {
              resolve({
                lng: result.position.lng,
                lat: result.position.lat,
              });
              return;
            }
            self.getBrowserLocation(resolve);
          });
          return;
        }
        self.getBrowserLocation(resolve);
      });
    },
    getBrowserLocation: function (resolve) {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          resolve({
            lng: pos.coords.longitude,
            lat: pos.coords.latitude,
          });
        },
        function () {
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    },
    applyNearestStationByUserLocation: function () {
      var self = this;
      if (!this.stations.length) return Promise.resolve(false);
      return this.getUserLocation().then(function (userPos) {
        if (userPos) {
          var nearest = findNearestMapStation(userPos, self.stations);
          if (nearest) {
            self.nearestStationRetry = false;
            self.selectStationOnMap(nearest);
            return true;
          }
        }
        self.nearestStationRetry = self.mapEntry === "vehicleTab";
        self.selectStationOnMap(self.stations[0]);
        return true;
      });
    },
    resolveStationProgressContext: function () {
      var classDate = this.lineClassDay && this.lineClassDay.ClassDate;
      if (!classDate) return { nowMs: Date.now() };
      var d = parseDateTime(classDate);
      return {
        classDate: d ? formatYmd(d) : String(classDate).substring(0, 10),
        nowMs: Date.now(),
      };
    },
    collectDepartPassedNodeGids: function (gps) {
      var vGid = this.normalizeGid(gps && gps.VehicleGID);
      var vNo = String((gps && gps.VehicleNo) || "").trim().toUpperCase();
      var gids = [];
      (this.departRecords || []).forEach(function (r) {
        if (!(r.SignInOperTime || r.StartCarTime)) return;
        var rGid = String(r.VehicleGID || "").trim().toUpperCase();
        var rNo = String(r.VehicleNo || "").trim().toUpperCase();
        var same = (vGid && rGid && vGid === rGid) || (vNo && rNo && vNo === rNo);
        if (!same) return;
        var nodeGid = String(r.StartNodeGID || "").trim();
        if (nodeGid) gids.push(nodeGid);
      });
      return gids;
    },
    resolveVehicleProgressContext: function (gps) {
      var base = this.resolveStationProgressContext();
      if (!gps) return base;
      return Object.assign({}, base, {
        departPassedNodeGids: this.collectDepartPassedNodeGids(gps),
      });
    },
    resolvePassStickyKey: function (vehicleGid, stationId) {
      return this.normalizeGid(vehicleGid) + "|" + stationId;
    },
    resolvePassedWithSticky: function (vehicleGid, selected, pos, gps) {
      var key = this.resolvePassStickyKey(vehicleGid, selected.id);
      var d = haversineDistanceM(pos, selected);
      var prevMin = this.stationMinDistMap[key];
      if (prevMin == null || d < prevMin) {
        this.$set(this.stationMinDistMap, key, d);
      }
      var motion = {
        angle: gps.Angle,
        speed: Number((gps.Speed != null ? gps.Speed : gps.Data && gps.Data.Speed) || 0),
      };
      var passedNow = hasVehiclePassedStationByRoute(
        pos,
        selected,
        this.stations,
        STATION_PASSED_M,
        motion,
        this.resolveVehicleProgressContext(gps)
      );
      var minD = this.stationMinDistMap[key];
      var leftAfterVisit = false;
      if (minD != null && minD < 800 && d > minD + 250) {
        var bearToStation = geoBearingToVehicleAngle(bearingDegrees(pos, selected));
        var angle = motion.angle;
        if (!isFinite(angle) || headingAngleDiff(angle, bearToStation) > 75) {
          leftAfterVisit = true;
        }
      }
      if (passedNow || leftAfterVisit) {
        this.$set(this.passedStickyMap, key, true);
        return true;
      }
      return !!this.passedStickyMap[key];
    },
    isStationPassedAtIndex: function (stationIdx, vehicleGid, gps, pos, vehicleProgress) {
      if (stationIdx < 0 || stationIdx >= this.stations.length) return false;
      var station = this.stations[stationIdx];
      if (isVehicleArrivedAtStationByDepartRecord(this.departRecords, vehicleGid, station.nodeGid)) {
        return true;
      }
      if (isVehiclePassedStationByLaterDepartRecords(this.departRecords, this.stations, vehicleGid, station)) {
        return true;
      }
      var stillApproaching =
        pos &&
        stationIdx > 0 &&
        isVehicleApproachingOnStationSegment(
          pos,
          this.stations[stationIdx - 1],
          station,
          1.2,
          vehicleProgress
        );
      if (stillApproaching) return false;
      if (pos && stationIdx > 0) {
        if (
          hasReachedOrPassedStationOnSegment(
            pos,
            this.stations[stationIdx - 1],
            station,
            STATION_PASSED_M,
            vehicleProgress
          )
        ) {
          return true;
        }
      }
      if (isStationSchedulePassed(this.stations, stationIdx, vehicleProgress)) return true;
      if (pos && gps && this.resolvePassedWithSticky(vehicleGid, station, pos, gps)) return true;
      return false;
    },
    isSelectedStationPassed: function (selected, vehicleGid, gps, pos, vehicleProgress) {
      var selectedIdx = -1;
      for (var i = 0; i < this.stations.length; i++) {
        if (this.stations[i].id === selected.id) {
          selectedIdx = i;
          break;
        }
      }
      if (selectedIdx < 0) return false;
      if (pos && selectedIdx > 0) {
        if (
          isVehicleApproachingOnStationSegment(
            pos,
            this.stations[selectedIdx - 1],
            selected,
            1.2,
            vehicleProgress
          )
        ) {
          return false;
        }
      }
      if (this.isStationPassedAtIndex(selectedIdx, vehicleGid, gps, pos, vehicleProgress)) {
        return true;
      }
      for (var j = selectedIdx + 1; j < this.stations.length; j++) {
        if (this.isStationPassedAtIndex(j, vehicleGid, gps, pos, vehicleProgress)) return true;
      }
      return false;
    },
    isVehicleAtStationForDisplay: function (pos, station, gps) {
      var distM = haversineDistanceM(pos, station);
      var speedKmh = Number(
        (gps && (gps.Speed != null ? gps.Speed : gps.Data && gps.Data.Speed)) || 0
      );
      return distM < STATION_DISPLAY_ARRIVED_M && speedKmh < STATION_DISPLAY_ARRIVED_MAX_SPEED_KMH;
    },
    resolveArrivedStatusLabel: function (arrivedHere, pos, selected, gps) {
      if (arrivedHere) return STATUS_LABEL_ARRIVED_DEPARTED;
      if (pos && this.isVehicleAtStationForDisplay(pos, selected, gps)) return STATUS_LABEL_ARRIVED;
      return STATUS_LABEL_ARRIVED_DEPARTED;
    },
    resolveGpsMotionText: function (gps) {
      if (!gps || !normalizeGpsPosition(gps)) {
        return { directionText: "", speedText: "" };
      }
      var direction = gps.Direction;
      var speed = Number((gps.Speed != null ? gps.Speed : gps.Data && gps.Data.Speed) || 0);
      return {
        directionText: direction || "",
        speedText: (isFinite(speed) ? speed : 0) + " km/h",
      };
    },
    matchGpsForPreview: function (preview, gpsList) {
      var i;
      if (preview.VehicleGID) {
        var key = this.normalizeGid(preview.VehicleGID);
        for (i = 0; i < gpsList.length; i++) {
          if (this.normalizeGid(gpsList[i].VehicleGID) === key) return gpsList[i];
        }
      }
      if (preview.VehicleNo) {
        var no = String(preview.VehicleNo).trim().toUpperCase();
        for (i = 0; i < gpsList.length; i++) {
          if (String(gpsList[i].VehicleNo || "").trim().toUpperCase() === no) return gpsList[i];
        }
      }
      return gpsList.length === 1 ? gpsList[0] : null;
    },
    buildEtaPointsForVehicle: function (gps, selected) {
      var pos = normalizeGpsPosition(gps);
      if (!pos) return [];
      return buildEtaRoutePointsToStation(pos, selected, this.stations, STATION_PASSED_M, {
        departPassedNodeGids: this.collectDepartPassedNodeGids(gps),
        motion: {
          angle: gps.Angle != null ? gps.Angle : gps.Data && gps.Data.Angle,
          speed: Number((gps.Speed != null ? gps.Speed : gps.Data && gps.Data.Speed) || 0),
        },
        progress: this.resolveVehicleProgressContext(gps),
      });
    },
    formatEtaArriveText: function (distanceM, durationS) {
      return "预计距离" + formatDistanceKm(distanceM) + "，" + formatDurationText(durationS) + "后到达";
    },
    resolveIncomingMetrics: function (gps, selected) {
      if (!gps || !normalizeGpsPosition(gps)) {
        return { etaText: "正在获取车辆定位..." };
      }
      var cache = this.vehicleApproachCache[gps.VehicleGID];
      var routePoints = this.buildEtaPointsForVehicle(gps, selected);
      var distanceM =
        cache && cache.nextStationId === selected.id && cache.distanceM
          ? cache.distanceM
          : estimateRouteChainDistanceM(routePoints);
      if (cache && cache.nextStationId === selected.id && cache.loading) {
        return { etaText: "正在计算预计到达..." };
      }
      if (cache && cache.nextStationId === selected.id && cache.distanceM && cache.durationS) {
        return { etaText: this.formatEtaArriveText(cache.distanceM, cache.durationS) };
      }
      this.refreshVehicleApproach(gps);
      var speed = Number(gps.Speed || 0);
      var durationS =
        speed >= 5
          ? Math.max(60, Math.round(distanceM / ((speed * 1000) / 3600)))
          : estimateDurationByDistanceM(distanceM);
      return { etaText: this.formatEtaArriveText(distanceM, durationS) };
    },
    refreshVehicleApproach: function (gps) {
      var self = this;
      if (this.isClassTaskFinished) return Promise.resolve();
      var selected = this.selectedStation;
      if (!selected || !this.departRecordsReady) return Promise.resolve();
      var pos = normalizeGpsPosition(gps);
      var vehicleProgress = this.resolveVehicleProgressContext(gps);
      if (this.isSelectedStationPassed(selected, gps.VehicleGID, gps, pos, vehicleProgress)) {
        return Promise.resolve();
      }
      if (!pos) return Promise.resolve();
      var key = gps.VehicleGID;
      var cache = this.vehicleApproachCache[key];
      var token = (this.approachRefreshTokens[key] || 0) + 1;
      this.approachRefreshTokens[key] = token;
      this.$set(this.vehicleApproachCache, key, {
        nextStationId: selected.id,
        distanceM: cache && cache.nextStationId === selected.id ? cache.distanceM : 0,
        durationS: cache && cache.nextStationId === selected.id ? cache.durationS : 0,
        loading: true,
        updatedAt: Date.now(),
        vehicleLat: pos.lat,
        vehicleLng: pos.lng,
        source: (cache && cache.source) || "fallback",
      });
      var routePoints = this.buildEtaPointsForVehicle(gps, selected);
      return fetchAmapDrivingRouteChain(routePoints, { strategy: AMAP_ETA_ROUTE_STRATEGY }).then(
        function (result) {
          if (self.approachRefreshTokens[key] !== token) return;
          if (result && result.distanceM && result.durationS) {
            self.$set(self.vehicleApproachCache, key, {
              nextStationId: selected.id,
              distanceM: result.distanceM,
              durationS: result.durationS,
              loading: false,
              updatedAt: Date.now(),
              vehicleLat: pos.lat,
              vehicleLng: pos.lng,
              source: "amap",
            });
            return;
          }
          var distanceM = estimateRouteChainDistanceM(routePoints);
          var speed = Number(gps.Speed || 0);
          var durationS =
            speed >= 5
              ? Math.max(60, Math.round(distanceM / ((speed * 1000) / 3600)))
              : estimateDurationByDistanceM(distanceM);
          self.$set(self.vehicleApproachCache, key, {
            nextStationId: selected.id,
            distanceM: distanceM,
            durationS: durationS,
            loading: false,
            updatedAt: Date.now(),
            vehicleLat: pos.lat,
            vehicleLng: pos.lng,
            source: "fallback",
          });
        }
      );
    },
    scheduleVehicleApproachRefresh: function (list) {
      var self = this;
      (list || []).forEach(function (item) {
        self.refreshVehicleApproach(item);
      });
    },
    buildStationVehicleRow: function (preview, gpsList, selected, taskFinished) {
      var gps = this.matchGpsForPreview(preview, gpsList);
      var vehicleGid = preview.VehicleGID || (gps && gps.VehicleGID);
      var vehicleNo = preview.VehicleNo || (gps && gps.VehicleNo);
      var pos = gps ? normalizeGpsPosition(gps) : null;
      var self = this;
      var anyVehicleRecord = (this.departRecords || []).filter(function (r) {
        var rGid = String(r.VehicleGID || "").trim().toUpperCase();
        var vGid = String(vehicleGid || "").trim().toUpperCase();
        if (vGid && rGid && rGid === vGid) return true;
        var rNo = String(r.VehicleNo || "").trim().toUpperCase();
        var vNo = String(vehicleNo || "").trim().toUpperCase();
        return !!(vNo && rNo && rNo === vNo);
      })[0];
      var record = findVehicleStationDepartRecord(this.departRecords, vehicleGid, selected.nodeGid);
      var displayNo =
        preview.VehicleNo ||
        (record && record.VehicleNo) ||
        (anyVehicleRecord && anyVehicleRecord.VehicleNo) ||
        (gps && gps.VehicleNo);
      var seatCount =
        preview.PassengerSeatCount != null
          ? preview.PassengerSeatCount
          : record && record.PassengerSeatCount != null
          ? record.PassengerSeatCount
          : anyVehicleRecord && anyVehicleRecord.PassengerSeatCount;
      var driverName =
        preview.Driver1Name ||
        (record && record.Driver1Name) ||
        (anyVehicleRecord && anyVehicleRecord.Driver1Name);
      var driverTel = preview.Driver1Tel;
      var motion = this.resolveGpsMotionText(gps);
      if (taskFinished) {
        return {
          status: "ended",
          vehicleGid: vehicleGid,
          vehicleNo: displayNo,
          seatCount: seatCount,
          driverName: driverName,
          driverTel: driverTel,
          directionText: motion.directionText,
          speedText: motion.speedText,
          statusLabel: "本班次任务已结束",
          etaText: "",
        };
      }
      var vehicleProgress = this.resolveVehicleProgressContext(gps);
      var arrivedHere = isVehicleArrivedAtStationByDepartRecord(
        this.departRecords,
        vehicleGid,
        selected.nodeGid
      );
      var arrived = this.isSelectedStationPassed(selected, vehicleGid, gps, pos, vehicleProgress);
      if (arrived) {
        return {
          status: "arrived",
          vehicleGid: vehicleGid,
          vehicleNo: displayNo,
          seatCount: seatCount,
          driverName: driverName,
          driverTel: driverTel,
          directionText: motion.directionText,
          speedText: motion.speedText,
          statusLabel: this.resolveArrivedStatusLabel(arrivedHere, pos, selected, gps),
          etaText: "",
        };
      }
      if (!this.departRecordsReady) {
        return {
          status: "enroute",
          vehicleGid: vehicleGid,
          vehicleNo: displayNo,
          seatCount: seatCount,
          driverName: driverName,
          driverTel: driverTel,
          directionText: motion.directionText,
          speedText: motion.speedText,
          statusLabel: "查询到站状态...",
          etaText: "",
        };
      }
      if (!pos) {
        return {
          status: "enroute",
          vehicleGid: vehicleGid,
          vehicleNo: displayNo,
          seatCount: seatCount,
          driverName: driverName,
          driverTel: driverTel,
          directionText: motion.directionText,
          speedText: motion.speedText,
          statusLabel: "获取定位中",
          etaText: "正在获取车辆定位...",
        };
      }
      var distToStationM = haversineDistanceM(pos, selected);
      var nearStation = distToStationM <= STATION_INCOMING_M;
      var incoming = this.resolveIncomingMetrics(gps, selected);
      return {
        status: nearStation ? "incoming" : "enroute",
        vehicleGid: vehicleGid,
        vehicleNo: displayNo,
        seatCount: seatCount,
        driverName: driverName,
        driverTel: driverTel,
        directionText: motion.directionText,
        speedText: motion.speedText,
        statusLabel: nearStation ? "即将到站" : "行驶中",
        etaText: incoming.etaText,
      };
    },
    parseGpsTime: function (time) {
      if (time == null || time === "") return null;
      if (time instanceof Date || typeof time === "number") {
        return parseDateTime(time);
      }
      var trimmed = String(time).trim();
      if (!trimmed) return null;
      var hasDate = /^\d{4}[-/]/.test(trimmed) || trimmed.indexOf("T") > -1;
      if (!hasDate && /^\d{1,2}:\d{2}/.test(trimmed)) {
        var datePart =
          this.lineClassDay && this.lineClassDay.ClassDate
            ? String(this.lineClassDay.ClassDate).substring(0, 10)
            : formatYmd(new Date());
        return parseDateTime(datePart + " " + trimmed);
      }
      return parseDateTime(trimmed);
    },
    formatGpsTime: function (time) {
      var parsed = this.parseGpsTime(time);
      return parsed ? formatHms(parsed) : time ? String(time) : "";
    },
    timeAgoFromReceivedAt: function (receivedAtMs) {
      if (!receivedAtMs) return "";
      var diffS = Math.max(0, Math.round((Date.now() - receivedAtMs) / 1000));
      if (diffS < 60) return diffS + "秒前更新";
      if (diffS < 3600) return Math.floor(diffS / 60) + "分钟前更新";
      return Math.floor(diffS / 3600) + "小时前更新";
    },
    refreshLastGpsUpdateText: function () {
      this.refreshGpsAgoText();
    },
    refreshGpsAgoText: function () {
      var t = this.lastGpsUpdateAt;
      var ago = this.timeAgoFromReceivedAt(this.lastGpsReceivedAt);
      if (t) {
        this.lastGpsUpdateText = this.formatGpsTime(t) + (ago ? " " + ago : "");
        return;
      }
      this.lastGpsUpdateText = ago || (this.vehiclePositions.length ? "刚刚更新" : "");
    },
    triggerGpsUpdatePulse: function () {
      var self = this;
      this.gpsUpdatePulse = true;
      setTimeout(function () {
        self.gpsUpdatePulse = false;
      }, 1200);
    },
    updateLastGpsUpdateText: function (list) {
      var self = this;
      var times = (list || [])
        .map(function (item) {
          return pickGpsTime(item);
        })
        .filter(function (t) {
          return t != null && t !== "";
        });
      var parsedMs = times
        .map(function (t) {
          var d = self.parseGpsTime(t);
          return d ? d.getTime() : 0;
        })
        .filter(function (ms) {
          return ms > 0;
        });
      var bestMs = parsedMs.length ? Math.max.apply(null, parsedMs) : 0;
      var bestTimeStr = "";
      if (bestMs) {
        for (var i = 0; i < times.length; i++) {
          var d = this.parseGpsTime(times[i]);
          if (d && d.getTime() === bestMs) {
            bestTimeStr = times[i];
            break;
          }
        }
        if (!bestTimeStr) bestTimeStr = times[0];
      }
      this.lastGpsUpdateAt = bestTimeStr;
      this.lastGpsReceivedAt = Date.now();
      this.refreshGpsAgoText();
      this.triggerGpsUpdatePulse();
      if (this.gpsUpdateTimer) clearInterval(this.gpsUpdateTimer);
      this.gpsUpdateTimer = setInterval(function () {
        self.refreshGpsAgoText();
      }, 1000);
    },
    applyGpsPayload: function (list) {
      this.vehiclePositions = list || [];
      this.updateLastGpsUpdateText(this.vehiclePositions);
      this.scheduleVehicleApproachRefresh(this.vehiclePositions);
      if (this.isViewportLocked) return;
      if (this.vehiclePositions.length && this.map) {
        var pos = normalizeGpsPosition(this.vehiclePositions[0]);
        if (pos) this.map.setCenter([pos.lng, pos.lat]);
      }
    },
    initSignalR: function () {
      var self = this;
      if (this.isClassTaskFinished) {
        this.closeSignalR(true);
        this.signalRStatus = "ended";
        this.wsTitle = "本班次任务已结束，不再拉取实时定位";
        return;
      }
      this.signalRStatus = "connecting";
      this.closeSignalR(true);
      var signalrUrl =
        process.env.NODE_ENV === "production" ? "https://class.busboss.cn/signalr" : "/signalr";
      this.ws = new VehicleWebSocket(
        signalrUrl,
        this.vehicleGIDList,
        function (data) {
          var ok = data && (data.success === true || data.Success === true);
          if (!ok) {
            self.wsTitle = (data && (data.msg || data.Msg)) || "获取车辆定位失败";
            return;
          }
          if (self.isClassTaskFinished) {
            self.closeSignalR(true);
            return;
          }
          self.signalRStatus = "connected";
          self.wsTitle = "正在持续查询车辆位置...";
          var list = data.data || data.Data || [];
          self.applyGpsPayload(Array.isArray(list) ? list : []);
        },
        {
          qs: {
            _Authorization: window.localStorage.getItem("_cyw_token"),
          },
          onOpen: function () {
            self.signalRStatus = "connected";
            self.signalRDisconnectModalShown = false;
            self.wsTitle = "正在持续查询车辆位置...";
          },
          onError: function () {
            if (self.pageDestroyed || self.isClassTaskFinished) return;
            self.signalRStatus = "error";
            self.wsTitle = "服务器连接错误，请刷新重试";
            self.handleSignalRDisconnected();
          },
          onClose: function () {
            if (self.signalRCloseSilentCount > 0) {
              self.signalRCloseSilentCount -= 1;
              return;
            }
            if (self.pageDestroyed) return;
            self.handleSignalRDisconnected();
          },
        }
      );
    },
    handleSignalRDisconnected: function () {
      if (this.pageDestroyed || this.isClassTaskFinished) return;
      if (this.signalRStatus !== "error") this.signalRStatus = "disconnected";
      this.wsTitle = "连接已关闭，请刷新重试";
      if (this.signalRDisconnectModalShown) return;
      this.signalRDisconnectModalShown = true;
    },
    closeSignalR: function (silent) {
      if (!this.ws) return;
      if (silent) this.signalRCloseSilentCount += 1;
      this.ws.close();
      this.ws = null;
    },
    refreshSignalR: function () {
      if (this.isClassTaskFinished) {
        this.$toast("本班次任务已结束");
        return;
      }
      this.signalRDisconnectModalShown = false;
      this.initSignalR();
      this.$toast("正在刷新定位...");
    },
    zoomIn: function () {
      if (!this.map) return;
      var zoom = this.map.getZoom();
      this.map.setZoom(Math.min(20, zoom + 1));
      this.mapZoom = this.map.getZoom();
    },
    zoomOut: function () {
      if (!this.map) return;
      var zoom = this.map.getZoom();
      this.map.setZoom(Math.max(3, zoom - 1));
      this.mapZoom = this.map.getZoom();
    },
    recenterToVehicle: function () {
      var gps = this.vehiclePositions && this.vehiclePositions[0];
      var pos = gps ? normalizeGpsPosition(gps) : null;
      if (!pos || !this.map) {
        this.$toast("暂无车辆位置");
        return;
      }
      this.isManualCenter = true;
      this.map.setZoomAndCenter(16, [pos.lng, pos.lat]);
    },
    clearMarkers: function (list) {
      if (!list) return;
      for (var i = 0; i < list.length; i++) {
        list[i].setMap(null);
      }
    },
    directionToAngle: function (direction) {
      switch (direction) {
        case "东":
          return 0;
        case "东南":
          return 45;
        case "南":
          return 90;
        case "西南":
          return 135;
        case "西":
          return 180;
        case "西北":
          return 225;
        case "北":
          return 275;
        case "东北":
          return 315;
        default:
          return 0;
      }
    },
    getVehicleMarkerAngle: function (item) {
      if (item && item.Direction) return this.directionToAngle(item.Direction);
      var raw = item && (item.Angle != null ? item.Angle : item.Data && item.Data.Angle);
      var angle = Number(raw);
      return isFinite(angle) ? geoBearingToVehicleAngle(angle) : 0;
    },
    renderMarkers: function () {
      if (!this.map || !this.AMap) return;
      var self = this;
      this.clearMarkers(this.stationMarkers);
      this.clearMarkers(this.vehicleMarkers);
      this.stationMarkers = [];
      this.vehicleMarkers = [];
      (this.stations || []).forEach(function (st) {
        var selected = st.id === self.selectedStationId;
        var marker = new self.AMap.Marker({
          position: [st.lng, st.lat],
          content: self.buildStationMarkerHtml(st),
          offset: new self.AMap.Pixel(-STATION_ICON_SIZE / 2, -STATION_ICON_SIZE),
          zIndex: selected ? 120 : 80,
        });
        marker.on("click", function () {
          self.selectStationOnMap(st);
        });
        marker.setMap(self.map);
        self.stationMarkers.push(marker);
      });
      (this.vehiclePositions || []).forEach(function (item) {
        var pos = normalizeGpsPosition(item);
        if (!pos) return;
        var angle = self.getVehicleMarkerAngle(item);
        var marker = new self.AMap.Marker({
          position: [pos.lng, pos.lat],
          content: self.buildVehicleMarkerHtml(item, angle),
          offset: new self.AMap.Pixel(-STATION_ICON_SIZE / 2, -STATION_ICON_SIZE / 2),
          zIndex: 130,
          map: self.map,
        });
        self.vehicleMarkers.push(marker);
      });
    },
  },
};
</script>

<style scoped>
.vehicle-map-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: #f5f5f5;
  text-align: left;
}
.vehicle-map-body {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: calc(100% - 45px);
}
.vehicle-map-canvas {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.vehicle-map-top {
  position: absolute;
  left: 10px;
  right: 12px;
  top: 10px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}
.vehicle-map-top * {
  pointer-events: auto;
}
.vehicle-map-top__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.vehicle-map-card {
  min-width: 0;
  flex: 1;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  padding: 10px 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
.vehicle-map-card__title {
  overflow: hidden;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vehicle-map-card__sub {
  margin-top: 2px;
  overflow: hidden;
  color: #666;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vehicle-map-refresh {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.vehicle-map-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ffd591;
  border-radius: 6px;
  background: #fff7e6;
  padding: 8px 10px;
  color: #ad6800;
  font-size: 12px;
}
.vehicle-map-gps {
  align-self: flex-start;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  padding: 6px 10px;
  color: #1a1a1a;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s;
}
.vehicle-map-gps.is-pulse {
  transform: scale(1.08);
}
.vehicle-map-controls {
  position: absolute;
  right: 12px;
  bottom: 220px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}
.vehicle-map-controls * {
  pointer-events: auto;
}
.vehicle-map-ctrl {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  color: #333;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.vehicle-map-ctrl--bus {
  background: #1677ff;
  color: #fff;
}
.vehicle-map-bottom {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 20px;
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  padding: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: left;
}
.vehicle-map-station {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  text-align: left;
  cursor: pointer;
}
.vehicle-map-station__name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vehicle-map-divider {
  height: 1px;
  margin: 8px 0;
  background: #f0f0f0;
}
.vehicle-map-empty {
  color: #999;
  font-size: 15px;
}
.vehicle-map-row.is-split {
  margin-top: 10px;
  border-top: 1px solid #f0f0f0;
  padding-top: 10px;
}
.vehicle-map-row__head,
.vehicle-map-row__driver,
.vehicle-map-row__status {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  text-align: left;
}
.vehicle-map-row__bus {
  color: #ed9f33;
  font-size: 18px;
}
.vehicle-map-row__plate {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vehicle-map-row__seat {
  color: #666;
  font-size: 14px;
  font-weight: normal;
}
.vehicle-map-row__motion {
  flex-shrink: 0;
  color: #666;
  font-size: 14px;
}
.vehicle-map-row__driver {
  margin-top: 6px;
}
.vehicle-map-row__driver-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #333;
  font-size: 15px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.vehicle-map-row__tel {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #3c9cff;
  font-size: 14px;
}
.vehicle-map-row__status {
  margin-top: 6px;
}
.vehicle-map-row__eta {
  color: #1677ff;
  font-size: 15px;
  font-weight: 500;
}
</style>

<style>
.amap-marker-label {
  border: none !important;
  background: transparent !important;
  padding: 0 !important;
}
.vm-map-label {
  transform: translate(-50%, -100%);
  margin-bottom: 4px;
  border-radius: 4px;
  background: #fff;
  padding: 2px 8px;
  color: #1a1a1a;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
}
.vm-station-marker {
  position: relative;
  width: 32px;
  height: 32px;
}
.vm-station-name {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 2;
  transform: translateX(-50%);
  margin-bottom: 2px;
  border-radius: 4px;
  background: #fff;
  padding: 1px 6px;
  color: #1a1a1a;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  pointer-events: none;
}
.vm-station-marker__icon {
  display: block;
  width: 32px;
  height: 32px;
}
.vm-vehicle-marker {
  position: relative;
  width: 32px;
  height: 32px;
}
.vm-vehicle-plate {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 2;
  transform: translateX(-50%);
  margin-bottom: 2px;
  pointer-events: none;
  border-radius: 4px;
  background: #fff;
  padding: 1px 6px;
  color: #1a1a1a;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
}
.vm-vehicle-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.vm-vehicle-icon-box img {
  display: block;
  width: 32px;
  height: auto;
  transform-origin: center center;
}
.station-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  text-align: left;
}
.station-picker__header {
  position: relative;
  flex-shrink: 0;
  height: 48px;
  border-bottom: 1px solid #f5f5f5;
}
.station-picker__title {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 48px;
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 600;
  line-height: 48px;
  text-align: center;
  pointer-events: none;
}
.station-picker__close {
  position: absolute;
  top: 0;
  right: 0;
  left: auto;
  z-index: 1;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.station-picker__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom);
}
.station-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f5f5f5;
}
.station-picker__main {
  min-width: 0;
  flex: 1;
  text-align: left;
}
.station-picker__name {
  margin-bottom: 6px;
  overflow: hidden;
  color: #1a1a1a;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.station-picker__item.is-selected .station-picker__name {
  color: #1677ff;
}
</style>
